import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentYear } from "@/lib/currentYear";

interface SEOTemplates {
  homepage: {
    title: string;
    description: string;
    h1: string;
  };
  article_listing: {
    title: string;
    description: string;
    h1: string;
  };
  article_detail: {
    title_template: string;
    description_template: string;
    h1_template: string;
  };
  card_detail: {
    title_template: string;
    description_template: string;
    h1_template: string;
  };
}

interface URLStructure {
  cards_base: string;
  articles_base: string;
}

interface SiteSettings {
  domain: string;
  site_name: string;
  default_og_image: string;
  site_logo_url: string;
  site_logo_footer_url?: string;
  default_meta_description: string;
  language: string;
  currency?: string; // EUR, NOK, etc.
  twitter_handle?: string;
  default_author: string;
  organization_name?: string;
  organization_email?: string;
  organization_social?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
  cardFallbackLogo?: string;
  gtm_container_id?: string;
  cookiebot_id?: string;
  breadcrumb_translations?: {
    home: string;
    articles: string;
    cards: string;
  };
  seo_templates?: SEOTemplates;
  url_structure?: URLStructure;
}

const defaultSettings: SiteSettings = {
  domain: 'https://example.com',
  site_name: 'Kreditkarten Vergleich',
  default_og_image: '/og-image.jpg',
  site_logo_url: '',
  site_logo_footer_url: '',
  default_meta_description: 'Finden Sie Ihre perfekte Kreditkarte. Vergleichen Sie Zinsen, Jahresgebühren und Vorteile deutscher Kreditkarten.',
  language: 'de-DE',
  currency: 'EUR',
  default_author: 'Redaktion',
  twitter_handle: '',
  organization_name: '',
  organization_email: '',
  organization_social: {},
  cardFallbackLogo: '',
  gtm_container_id: '',
  cookiebot_id: '',
  breadcrumb_translations: {
    home: 'Startseite',
    articles: 'Ratgeber',
    cards: 'Kreditkarten'
  },
  seo_templates: {
    homepage: {
      get title() { return `Kreditkarten vergleichen & die beste Karte ${currentYear()} finden`; },
      description: "Vergleichen Sie Kreditkarten in Deutschland – finden Sie gebührenfreie, Reise- und Cashback-Karten mit Vorteilen, die zu Ihnen passen.",
      h1: "Finden Sie die passende Kreditkarte für Ihre Bedürfnisse"
    },
    article_listing: {
      title: "Ratgeber & Artikel zu Kreditkarten, Zinsen und Finanzen",
      description: "Lesen Sie hilfreiche Ratgeber rund um Kreditkarten, Zinsen, Sicherheit und Finanzentscheidungen.",
      h1: "Alle Artikel & Ratgeber"
    },
    article_detail: {
      title_template: "{title} – Ratgeber auf Kreditkarten.de",
      description_template: "{excerpt}. Lesen Sie Tipps zu Kreditkarten, Gebühren und Finanzentscheidungen.",
      h1_template: "{title}"
    },
    card_detail: {
      title_template: "{cardName} – Konditionen, Vorteile & Zinsen",
      description_template: "Alle Details zur {cardName}: effektiver Jahreszins, Gebühren, Vorteile und Bonusprogramme. Jetzt vergleichen & online beantragen.",
      h1_template: "{cardName}"
    }
  },
  url_structure: {
    cards_base: "kreditkarten",
    articles_base: "ratgeber"
  }
};

export const siteSettingsQueryOptions = {
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('page_settings')
        .select('settings')
        .eq('page_type', 'site_settings')
        .single();

      if (error) throw error;

      const s = data?.settings as any;
      return {
        domain: s?.domain || defaultSettings.domain,
        site_name: s?.site_name || defaultSettings.site_name,
        default_og_image: s?.default_og_image || defaultSettings.default_og_image,
        site_logo_url: s?.site_logo_url || defaultSettings.site_logo_url,
        site_logo_footer_url: s?.site_logo_footer_url || defaultSettings.site_logo_footer_url,
        default_meta_description: s?.default_meta_description || defaultSettings.default_meta_description,
        language: s?.language || defaultSettings.language,
        currency: s?.currency || defaultSettings.currency,
        default_author: s?.default_author || defaultSettings.default_author,
        twitter_handle: s?.twitter_handle || defaultSettings.twitter_handle,
        organization_name: s?.organization_name || defaultSettings.organization_name,
        organization_email: s?.organization_email || defaultSettings.organization_email,
        organization_social: s?.organization_social || defaultSettings.organization_social,
        cardFallbackLogo: s?.card_fallback_logo || defaultSettings.cardFallbackLogo,
        gtm_container_id: s?.gtm_container_id || defaultSettings.gtm_container_id,
        cookiebot_id: s?.cookiebot_id || defaultSettings.cookiebot_id,
        breadcrumb_translations: s?.breadcrumb_translations || defaultSettings.breadcrumb_translations,
        seo_templates: s?.seo_templates || defaultSettings.seo_templates,
        url_structure: s?.url_structure || defaultSettings.url_structure
      } as SiteSettings;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes - site settings change very rarely
    gcTime: 30 * 60 * 1000, // 30 minutes in cache
} as const;

export const useSiteSettings = () => {
  return useQuery(siteSettingsQueryOptions);
};
